import type { IElementID, Project } from '@shared/models/project.model';
import {
  revertUndoRedoStatesAfterFailedEnrichedCommit,
  setUndoRedoSuppressedForInFlightPersist,
  syncUndoRedoStatesAfterLocalEnrichedCommit,
} from '@shared/helpers/undo-redo.helpers';

type PersistSkipEnrichmentSnapshotParams = {
  prevProject: Project;
  written: Project;
  selectedVersionId: IElementID | undefined;
  persist: () => Promise<Project>;
  recover?: (error: unknown) => Promise<Project | undefined>;
  revertStore: (project: Project) => void;
  onOptimisticLocalUpdate?: () => void;
  onEnriched?: () => void;
  onEnrichFailed?: () => void;
};

/**
 * Skip-enrichment persist after the optimistic local write.
 *
 * Undo is recorded immediately so the undo-init effect does not treat the write as a
 * second edit, then hidden until persist settles so Cmd+Z cannot race the PUT.
 * Suppression is nested: overlapping skip-enrichment PUTs stay hidden until the last
 * one settles.
 * `onEnriched` waits until persist succeeds so snackbar Undo is not enabled during
 * the PUT. On failure: drop that undo frame, restore `prevProject` in the store,
 * then `onEnrichFailed`.
 */
export const persistSkipEnrichmentSnapshot = async ({
  prevProject,
  written,
  selectedVersionId,
  persist,
  recover,
  revertStore,
  onOptimisticLocalUpdate,
  onEnriched,
  onEnrichFailed,
}: PersistSkipEnrichmentSnapshotParams): Promise<Project> => {
  syncUndoRedoStatesAfterLocalEnrichedCommit(
    prevProject,
    written,
    selectedVersionId,
  );
  setUndoRedoSuppressedForInFlightPersist(true);
  onOptimisticLocalUpdate?.();
  try {
    const persisted = await persist();
    setUndoRedoSuppressedForInFlightPersist(false);
    onEnriched?.();
    return persisted;
  } catch (error) {
    let failure = error;
    try {
      const recovered = await recover?.(error);
      if (recovered) {
        setUndoRedoSuppressedForInFlightPersist(false);
        onEnriched?.();
        return recovered;
      }
    } catch (recoveryError) {
      failure = recoveryError;
    }
    // Guard the init effect before the store snap-back so it is not recorded as an edit.
    revertUndoRedoStatesAfterFailedEnrichedCommit(prevProject, written);
    revertStore(prevProject);
    setUndoRedoSuppressedForInFlightPersist(false);
    onEnrichFailed?.();
    throw failure;
  }
};
